
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Loader2, Database, RotateCw, ExternalLink, Settings } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type Website } from "@/services/wordpressService";
import { EditWebsiteCredentialsDialog } from "@/components/wordpress/EditWebsiteCredentialsDialog";

interface WebsiteListViewProps {
  websites: Website[];
  isLoading: Record<string, boolean>;
  onBackupNow: (websiteId: string) => void;
  onViewBackups: (websiteId: string) => void;
  onRestore: (websiteId: string) => void;
}

const WebsiteListView: React.FC<WebsiteListViewProps> = ({
  websites,
  isLoading,
  onBackupNow,
  onViewBackups,
  onRestore,
}) => {
  const [editDialogState, setEditDialogState] = useState<{
    isOpen: boolean;
    websiteId: string;
    websiteTitle: string;
    websiteUrl: string;
  }>({
    isOpen: false,
    websiteId: "",
    websiteTitle: "",
    websiteUrl: "",
  });

  // Function to get platform-specific badge style
  const getPlatformBadgeStyle = (platform: "wordpress" | "shopify" | "woocommerce") => {
    switch (platform) {
      case "wordpress":
        return "bg-blue-100 text-blue-800";
      case "shopify":
        return "bg-green-100 text-green-800";
      case "woocommerce":
        return "bg-purple-100 text-purple-800";
    }
  };

  const openEditDialog = (website: Website) => {
    setEditDialogState({
      isOpen: true,
      websiteId: website.id,
      websiteTitle: website.title,
      websiteUrl: website.url,
    });
  };

  const closeEditDialog = () => {
    setEditDialogState({
      isOpen: false,
      websiteId: "",
      websiteTitle: "",
      websiteUrl: "",
    });
  };

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">Website</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead>Last Backup</TableHead>
              <TableHead>Next Backup</TableHead>
              <TableHead>Storage</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {websites.map((website) => (
              <TableRow key={website.id}>
                <TableCell className="font-medium">
                  <div>
                    <div className="font-medium">{website.title}</div>
                    <div className="text-sm text-muted-foreground flex items-center">
                      <ExternalLink className="h-3 w-3 mr-1" />
                      {website.url}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlatformBadgeStyle(website.platform)}`}>
                    {website.platform}
                  </span>
                </TableCell>
                <TableCell>{website.lastBackup}</TableCell>
                <TableCell>{website.nextBackup}</TableCell>
                <TableCell>
                  <div className="w-full max-w-24">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium">{website.storageUsed}%</span>
                    </div>
                    <Progress value={website.storageUsed} className="h-1.5" />
                  </div>
                </TableCell>
                <TableCell>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    website.status === "active" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                  }`}>
                    {website.status === "active" ? "Active" : "Paused"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!!isLoading[website.id]}
                      onClick={() => onBackupNow(website.id)}
                    >
                      {isLoading[website.id] ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Database className="h-3 w-3" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                    >
                      <Link to={`/backup-history/${website.id}`}>
                        View
                      </Link>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onRestore(website.id)}
                    >
                      <RotateCw className="h-3 w-3" />
                    </Button>
                    {website.platform === "wordpress" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(website)}
                      >
                        <Settings className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            
            {websites.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <div>
                    <p className="text-muted-foreground mb-4">No websites found</p>
                    <Button asChild variant="outline">
                      <Link to="/add-website">Add Website</Link>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Credentials Dialog */}
      <EditWebsiteCredentialsDialog
        websiteId={editDialogState.websiteId}
        websiteTitle={editDialogState.websiteTitle}
        websiteUrl={editDialogState.websiteUrl}
        isOpen={editDialogState.isOpen}
        onClose={closeEditDialog}
      />
    </>
  );
};

export default WebsiteListView;
